import React, { useEffect, useState } from "react";
import { Plus, Pencil, Cylinder, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ugx = (n) => `UGX ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const FIELD_LABEL = { name: "Name", cylinder_kg: "Capacity (kg)", cylinder_m3: "Measured m³", cylinder_price: "Cylinder price", refill_cost: "Stabex refill cost", security_value: "Cylinder security" };
const empty = { name: "", cylinder_kg: "", cylinder_m3: "", cylinder_price: "", refill_cost: "", security_value: "" };

const slug = (t) => t.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");

const TypeCard = ({ t, onEdit }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`cyl-type-card-${slug(t)}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-sm bg-flame-50 text-flame-600 border border-flame-100 grid place-items-center"><Cylinder size={16} /></div>
        <div>
          <div className="font-heading font-bold text-slate-900">{t.name}</div>
          <div className="text-[11px] text-slate-500">{t.household_count} household{t.household_count === 1 ? "" : "s"}{t.is_default ? " · default" : ""}</div>
        </div>
      </div>
      <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => onEdit(t)} data-testid={`cyl-type-edit-${slug(t)}`}>
        <Pencil size={12} className="mr-1" /> Edit
      </Button>
    </div>
    <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
      <Stat label="Capacity" value={`${t.cylinder_kg} kg`} />
      <Stat label="Measured gas" value={`${t.cylinder_m3} m³`} sub={`${t.m3_per_kg} m³/kg`} />
      <Stat label="Cylinder price" value={ugx(t.cylinder_price)} />
      <Stat label="Price per m³" value={ugx(t.price_per_m3)} sub={`${ugx(t.price_per_kg)} / kg`} tone="text-brand-700" testId={`cyl-type-price-m3-${slug(t)}`} />
      <Stat label="Stabex refill cost" value={ugx(t.refill_cost)} testId={`cyl-type-refill-${slug(t)}`} />
      <Stat label="Cylinder security" value={ugx(t.security_value)} sub="bank-guarantee cover" testId={`cyl-type-security-${slug(t)}`} />
    </div>
    <div className="text-[10px] text-slate-400 mt-4 pt-3 border-t border-slate-100" data-testid={`cyl-type-updated-${slug(t)}`}>
      Last updated {new Date(t.updated_at).toLocaleString()}{t.updated_by ? ` by ${t.updated_by}` : ""}
    </div>
  </div>
);

const Stat = ({ label, value, sub, tone = "text-slate-900", testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`font-heading text-base font-bold tabular-nums mt-0.5 ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-400 tabular-nums">{sub}</div>}
  </div>
);

const TypeDialog = ({ editing, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name, cylinder_kg: String(editing.cylinder_kg), cylinder_m3: String(editing.cylinder_m3), cylinder_price: String(editing.cylinder_price), refill_cost: String(editing.refill_cost ?? ""), security_value: String(editing.security_value ?? "") } : empty);
  }, [open, editing]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const m3 = Number(form.cylinder_m3) || 0, price = Number(form.cylinder_price) || 0, kg = Number(form.cylinder_kg) || 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !(kg > 0) || !(m3 > 0) || !(price > 0)) { toast.error("All fields are required and must be positive"); return; }
    setSaving(true);
    try {
      const body = { name: form.name.trim(), cylinder_kg: kg, cylinder_m3: m3, cylinder_price: price,
        refill_cost: Number(form.refill_cost) || 0, security_value: Number(form.security_value) || 0 };
      const { data } = editing ? await api.put(`/cylinder-types/${editing.id}`, body) : await api.post("/cylinder-types", body);
      toast.success(editing ? `Saved${data.meters_repriced ? ` — ${data.meters_repriced} meter(s) re-priced` : ""}` : "Cylinder type created");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="cyl-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "New cylinder type"}</DialogTitle>
          <DialogDescription>Price per m³ = cylinder price ÷ measured m³. Applies to future payments of households on this type.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="rounded-sm mt-1" data-testid="cyl-type-name" placeholder="e.g. 13 kg" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Capacity (kg)</Label><Input type="number" step="0.01" value={form.cylinder_kg} onChange={set("cylinder_kg")} className="rounded-sm mt-1" data-testid="cyl-type-kg" /></div>
            <div><Label>Measured m³ (full cylinder)</Label><Input type="number" step="0.001" value={form.cylinder_m3} onChange={set("cylinder_m3")} className="rounded-sm mt-1" data-testid="cyl-type-m3" /></div>
          </div>
          <div><Label>Full cylinder price (UGX)</Label><Input type="number" step="1" value={form.cylinder_price} onChange={set("cylinder_price")} className="rounded-sm mt-1" data-testid="cyl-type-price" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Stabex refill cost (UGX)</Label><Input type="number" step="1" value={form.refill_cost} onChange={set("refill_cost")} className="rounded-sm mt-1" data-testid="cyl-type-refill" /></div>
            <div><Label>Cylinder security (UGX)</Label><Input type="number" step="1" value={form.security_value} onChange={set("security_value")} className="rounded-sm mt-1" data-testid="cyl-type-security" /></div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600 tabular-nums" data-testid="cyl-type-derived">
            Price per m³: <span className="font-semibold text-slate-900">{m3 > 0 ? ugx(price / m3) : "—"}</span>
            <span className="mx-2 text-slate-300">|</span>
            Price per kg: <span className="font-semibold text-slate-900">{kg > 0 ? ugx(price / kg) : "—"}</span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-brand-600 hover:bg-brand-700" data-testid="cyl-type-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const fmtV = (f, v) => (v === null || v === undefined ? "—" : ["cylinder_price", "refill_cost", "security_value"].includes(f) ? ugx(v) : String(v));

const LogTable = ({ logs }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cyl-type-logs">
    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
      <History size={12} /> Configuration log
    </div>
    <div className="max-h-[360px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
          <tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Changes</th><th className="text-left px-4 py-2">By</th></tr>
        </thead>
        <tbody>
          {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No changes yet.</td></tr>}
          {logs.map((l, i) => (
            <tr key={l.id} className="border-t border-slate-100 align-top" data-testid={`cyl-log-row-${i}`}>
              <td className="px-4 py-2 font-mono text-slate-500 whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
              <td className="px-4 py-2 text-slate-800 font-medium">{l.type_name}</td>
              <td className="px-4 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold border ${l.action === "CREATE" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{l.action}</span></td>
              <td className="px-4 py-2 text-slate-700 space-y-0.5">
                {(l.changes || []).map((c) => (
                  <div key={c.field} className="tabular-nums">
                    <span className="text-slate-500">{FIELD_LABEL[c.field] || c.field}:</span>{" "}
                    {l.action === "CREATE" ? fmtV(c.field, c.new) : <><span className="line-through text-slate-400">{fmtV(c.field, c.old)}</span> → <span className="font-semibold">{fmtV(c.field, c.new)}</span></>}
                  </div>
                ))}
              </td>
              <td className="px-4 py-2 text-slate-600">{l.user_name || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const CylinderTypesPanel = () => {
  const [types, setTypes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const [t, l] = await Promise.all([api.get("/cylinder-types"), api.get("/cylinder-types/logs")]);
      setTypes(t.data.data || []); setLogs(l.data.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Failed to load cylinder types"); }
  };
  useEffect(() => { load(); }, []);
  const openDialog = (t) => { setEditing(t); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="cyl-types-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-heading font-semibold text-slate-900">Cylinder types</div>
          <div className="text-xs text-slate-500">Each household is billed at the price per m³ of its cylinder type.</div>
        </div>
        <Button size="sm" className="rounded-sm bg-brand-600 hover:bg-brand-700" onClick={() => openDialog(null)} data-testid="cyl-type-add">
          <Plus size={14} className="mr-1" /> Add type
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{types.map((t) => <TypeCard key={t.id} t={t} onEdit={openDialog} />)}</div>
      <LogTable logs={logs} />
      <TypeDialog editing={editing} open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
};
